import type { Shift } from "../types/Shift";

/**
 * New jobId set for one shift after staged edits: `(current ∪ additions) \ removals`.
 * Order is current-first, then any newly-added ids in iteration order; duplicates collapse.
 */
export function mergeShiftJobs(
	current: readonly string[],
	additions: Iterable<string>,
	removals: Iterable<string>,
): string[] {
	const merged = new Set<string>(current);
	for (const id of additions) merged.add(id);
	for (const id of removals) merged.delete(id);
	return Array.from(merged);
}

/**
 * Apply staged additions/removals (each keyed by shift id) to a set of existing shifts, returning
 * a fresh Shift for every one actually touched (referenced by either map). Shifts that neither map
 * mentions are omitted — only changed shifts need an update call.
 *
 * The returned objects are plain spreads of the source shift with a new `jobIds` (not class
 * instances); the persistence adapter serializes the fields, so the prototype isn't needed.
 */
export function buildShiftJobUpdates(
	shifts: readonly Shift[],
	additions: ReadonlyMap<string, ReadonlySet<string>>,
	removals: ReadonlyMap<string, ReadonlySet<string>>,
): Shift[] {
	const touched = new Set<string>([...additions.keys(), ...removals.keys()]);
	const updates: Shift[] = [];
	for (const shiftId of touched) {
		const shift = shifts.find((s) => s.id === shiftId);
		if (!shift) continue;
		const adds = additions.get(shiftId) ?? new Set<string>();
		const removes = removals.get(shiftId) ?? new Set<string>();
		updates.push({ ...shift, jobIds: mergeShiftJobs(shift.jobIds, adds, removes) } as Shift);
	}
	return updates;
}
